import type { ClipMetadata, ReelSectionOrigin } from '@src/types.ts/story';

/**
 * How different a Section's current length must be from its source clip's
 * before it counts as trimmed/edited.
 *
 * Deliberately NOT the Add Clips picker's `RANGE_MATCH_TOLERANCE_SECONDS`
 * (0.25), despite the family resemblance — the two answer different questions
 * and are free to drift. That one decides whether two ranges are *the same
 * range*, where being wrong mismatches a clip; this one decides whether a
 * creator has *edited* a Section, where being wrong puts a "trimmed" note on a
 * clip nobody touched. The looser value here is the cost of a word's worth of
 * boundary noise not reading as an edit.
 */
const TRIM_TOLERANCE_SECONDS = 0.35;

export type SectionSourceDelta = {
  /** The source clip's title at the time it was combined into the reel. */
  sourceTitle: string;
  /** The source clip's CURRENT full length, in seconds. */
  sourceDuration: number;
  /** True when the Section runs meaningfully shorter than its source clip. */
  isTrimmed: boolean;
};

/**
 * How a Section differs from the clip it was created from — the signal a
 * tooltip carries so the instance-vs-asset nuance never needs UI of its own.
 *
 * Read-time derivation only, nothing stored: the Section's current duration
 * against the source clip's current duration. That catches end-trims and
 * interior word cuts alike (both shrink the Section). A Section trimmed at
 * its START usually loses its origin match upstream (deliberate in
 * `matchStoredSubClips` — better no lineage than the wrong one) and arrives
 * here with no origin, so it shows nothing rather than something wrong.
 *
 * Returns null when the lineage is unknown or the source clip has left the
 * story — no origin, no claim.
 */
export function deriveSectionSourceDelta(
  section: { duration: number },
  origin: readonly ReelSectionOrigin[] | undefined,
  sourceClipJson: ClipMetadata | undefined,
): SectionSourceDelta | null {
  const source = origin?.[0];
  if (!source || !sourceClipJson) return null;
  const sourceDuration = sourceClipJson.duration;
  if (!Number.isFinite(sourceDuration) || sourceDuration <= 0) return null;
  return {
    sourceTitle: source.title,
    sourceDuration,
    isTrimmed: sourceDuration - section.duration > TRIM_TOLERANCE_SECONDS,
  };
}
